/**
 * PIV Slot Status Card Component
 *
 * Displays the current status of a PIV slot and allows configuration
 */

import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Alert, AlertTitle } from '@material-ui/lab';
import WarningIcon from '@material-ui/icons/Warning';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2)
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  statusIcon: {
    marginRight: theme.spacing(2)
  },
  slotInfo: {
    flexGrow: 1
  },
  certInfoBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  warningAlert: {
    marginTop: theme.spacing(2)
  },
  algorithmSelect: {
    marginTop: theme.spacing(2)
  },
  certInfoLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  }
}));

/**
 * Algorithm options for key generation
 */
const KEY_ALGORITHMS = [
  { value: '07', label: 'RSA-2048', description: 'Most compatible, recommended' },
  { value: '11', label: 'ECC P-256', description: 'Modern, efficient' },
  { value: '14', label: 'ECC P-384', description: 'High security elliptic curve' },
  { value: '05', label: 'RSA-1024', description: 'Deprecated, not recommended' },
  { value: '06', label: 'RSA-3072', description: 'Requires firmware support' },
  { value: '16', label: 'RSA-4096', description: 'Requires firmware support' }
];

function PIVSlotStatusCard({ slot, status, config, onConfigChange }) {
  const classes = useStyles();

  /**
   * Get icon based on slot status
   */
  const getStatusIcon = () => {
    const iconProps = { fontSize: 'large', className: classes.statusIcon };

    switch (status.status) {
      case 'complete':
        return <CheckCircleIcon {...iconProps} style={{ color: '#4caf50' }} />;
      case 'key_only':
        return <WarningIcon {...iconProps} style={{ color: '#ff9800' }} />;
      case 'empty':
        return <RadioButtonUncheckedIcon {...iconProps} style={{ color: '#9e9e9e' }} />;
      case 'error':
        return <ErrorIcon {...iconProps} style={{ color: '#f44336' }} />;
      default:
        return <HelpIcon {...iconProps} />;
    }
  };

  /**
   * Get chip color based on status
   */
  const getStatusColor = () => {
    switch (status.status) {
      case 'complete':
        return 'primary';
      case 'key_only':
        return 'secondary';
      case 'empty':
        return 'default';
      case 'error':
        return 'secondary';
      default:
        return 'default';
    }
  };

  /**
   * Get human-readable status label
   */
  const getStatusLabel = () => {
    const labels = {
      'complete': 'Configured',
      'key_only': 'Key Only (No Certificate)',
      'empty': 'Empty',
      'cert_only': 'Certificate Only (No Key)',
      'error': 'Error'
    };
    return labels[status.status] || 'Unknown';
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Card variant="outlined" className={classes.card}>
      <CardContent>
        {/* Status Header */}
        <Box className={classes.statusHeader}>
          {getStatusIcon()}
          <Box className={classes.slotInfo}>
            <Typography variant="h6">{slot.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {slot.description}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel()}
            color={getStatusColor()}
            size="small"
          />
        </Box>

        {/* Current Certificate Information */}
        {status.status === 'complete' && status.certInfo && !status.certInfo.error && (
          <Box className={classes.certInfoBox}>
            <Typography variant="subtitle2" className={classes.certInfoLabel}>
              Current Certificate
            </Typography>
            <Typography variant="body2">
              <strong>Subject:</strong> {status.certInfo.subject}
            </Typography>
            <Typography variant="body2">
              <strong>Issuer:</strong> {status.certInfo.issuer}
            </Typography>
            <Typography variant="body2">
              <strong>Valid Until:</strong> {formatDate(status.certInfo.validTo)}
            </Typography>
            <Typography variant="body2">
              <strong>Algorithm:</strong> {status.certInfo.algorithm} ({status.certInfo.keySize} bits)
            </Typography>
            <Typography variant="body2">
              <strong>Fingerprint:</strong> {status.certInfo.fingerprint ? status.certInfo.fingerprint.substring(0, 16) + '...' : 'N/A'}
            </Typography>
          </Box>
        )}

        {/* Key Only Status */}
        {status.status === 'key_only' && (
          <Alert severity="info" style={{ marginBottom: 16 }}>
            This slot has a private key but no certificate. You may want to generate a certificate for this key.
          </Alert>
        )}

        {/* Error Status */}
        {status.status === 'error' && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            <AlertTitle>Error</AlertTitle>
            {status.error || 'Failed to check slot status'}
          </Alert>
        )}

        {/* Action Selection */}
        <FormControl fullWidth variant="outlined">
          <InputLabel>Action</InputLabel>
          <Select
            value={config.mode}
            onChange={(e) => onConfigChange(slot.id, 'mode', e.target.value)}
            label="Action"
          >
            <MenuItem value="skip">
              Skip (Keep Current)
            </MenuItem>
            <MenuItem value="generate">
              {status.status === 'empty' ? 'Generate New Key' : 'Replace with New Key'}
            </MenuItem>
            <MenuItem value="import">
              {status.status === 'empty' ? 'Import External Key' : 'Replace with Imported Key'}
            </MenuItem>
          </Select>
        </FormControl>

        {/* Data Loss Warning */}
        {config.mode !== 'skip' && status.status !== 'empty' && (
          <Alert severity="warning" className={classes.warningAlert}>
            <AlertTitle>Data Loss Warning</AlertTitle>
            This action will permanently replace the existing{' '}
            {status.status === 'complete' ? 'certificate and private key' : 'private key'}.
            Make sure you have backed up any important data.
          </Alert>
        )}

        {/* Algorithm Selection for Generation */}
        {config.mode === 'generate' && (
          <FormControl fullWidth variant="outlined" className={classes.algorithmSelect}>
            <InputLabel>Key Algorithm</InputLabel>
            <Select
              value={config.algorithm}
              onChange={(e) => onConfigChange(slot.id, 'algorithm', e.target.value)}
              label="Key Algorithm"
            >
              {KEY_ALGORITHMS.map(alg => (
                <MenuItem key={alg.value} value={alg.value}>
                  {alg.label} - {alg.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </CardContent>
    </Card>
  );
}

export default PIVSlotStatusCard;
